import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import ModalTemplate from "../../../../../components/modal/modalTemplate";
import { theme } from "../../../../../theme";
import {
    fetchPersonalDocumentTypeslist,
    uploadDocuments,
} from "../../../../approvalProcess/service";
import DivSpinner from "../../../../../components/divSpinner";
import styles from "../../../../approvalProcess/components/styles";
import {
    convertTimestampToFormat,
    formatBytes,
    truncateText,
} from "../../../../../utils";
import { commonSwal } from "../../../../../components/swalAlert/swalAlert";
import { deleteDocument } from "../../../../documents/view/service";
import ActionBtnList from "../components/ActionBtnList";
import FilesView from "../../../../approvalProcess/components/filesView/FilesView";
import CheckLists from "../../CheckLists";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

// ✅ File status definitions
const STATUS_ICONS = {
    uploading: { icon: "upload", text: "UPLOADING...", class: "text-warning" },
    success: { icon: "verified", text: "UPLOADED", class: "text-success" },
    error: { icon: "error", text: "ERROR", class: "text-danger" },
    existing: { icon: "cloud_upload", text: "EXISTING", class: "text-warning" },
};

// ✅ Helper to map server docs to file objects
const mapServerDocsToFiles = (docs) =>
    docs.map((doc, i) => {
        const fileName = doc.ftm_personal_document_file_path.split("/").pop();
        const isImage = IMAGE_EXTENSIONS.some((ext) =>
            fileName.toLowerCase().endsWith(ext)
        );

        return {
            id: `${Date.now()}-${i}`,
            preview: doc.ftm_personal_document_file_path,
            fileName,
            isImage,
            status: "success",
            ftm_personal_document_file_id: doc.ftm_personal_document_file_id,
            type: "EXISTING",
        };
    });

// ✅ File preview component
const FileItem = ({ file, onRemove, handleFileViewOpen }) => {
    const status =
        file.type === "NEW"
            ? STATUS_ICONS[file.status] || STATUS_ICONS.uploading
            : STATUS_ICONS.existing;

    return (
        <div style={styles.thumb} onClick={() => handleFileViewOpen(file.id)}>
            {(file.status === "success" || file.status === "error") && (
                <button
                    onClick={() => onRemove(file)}
                    style={styles.topRightRemoveBtn}
                    title="Remove"
                >
                    &times;
                </button>
            )}

            {file.isImage ? (
                <img src={file.preview} alt={file.fileName} style={styles.thumbImage} />
            ) : (
                <div style={styles.pdfPreview}>
                    <i className="material-icons" style={{ fontSize: 48 }}>
                        file_present
                    </i>
                </div>
            )}

            <p className="mt-1 mb-0" style={styles.fileName}>
                {truncateText(file.fileName, 10)}
            </p>
            <p className="mb-1" style={styles.fileName}>
                {file?.file?.size ? formatBytes(file.file.size) : "N/A"}
            </p>

            <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <i className={`material-icons ${status.class}`}>{status.icon}</i>
                <span className="text-sm">{status.text}</span>
            </div>
        </div>
    );
};

const PersonalDocumentsFiles = ({
    show,
    handleClose,
    ftm_id,
    item,
    personalDocumentsHandleRefresh,
}) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const [acceptCheckList, setAcceptCheckList] = useState(false);

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileViewIndex, setFileViewIndex] = useState(0);

    const [fileUploading, setFileUploading] = useState(false);

    const handleFileViewOpen = (index) => {
        setFileViewIndex(index);
        setShowFileViewModal(true);
    }

    const handleFileViewClose = () => {
        setShowFileViewModal(false);
    }

    const documentIsExpired = item?.document?.ftm_personal_document_is_expired === 1;
    const isGetNewDocumentFile = item?.is_get_new_document_files;

    // ✅ Memoize title
    const title = useMemo(
        () => (
            <>
                <i
                    className={`material-icons text-xl me-2 text-${theme.main}`}
                >
                    {item?.document_type_icon}
                </i>
                {item?.document_type_name}
            </>
        ),
        [item?.document_type_icon, item?.document_type_name]
    );

    // ✅ Update file status safely
    const updateFileStatus = useCallback((id, updates) => {
        setFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
        );
    }, []);

    // ✅ Upload file handler
    const uploadFile = useCallback(
        async (fileObj) => {
            updateFileStatus(fileObj.id, { status: "uploading" });
            try {
                const response = await uploadDocuments({
                    ftm_id,
                    ftm_document_type_id: 0,
                    ftm_document_type: item?.document_type_code,
                    document_type: 0,
                    ftm_is_registering: 1,
                    ftm_document: fileObj.file,
                });

                if (response?.status === "Success") {
                    updateFileStatus(fileObj.id, {
                        status: "success",
                        ftm_personal_document_file_id:
                            response.ftm_personal_document_file_id,
                        document_path: response.document_path,
                    });
                } else {
                    updateFileStatus(fileObj.id, { status: "error" });
                }
            } catch {
                updateFileStatus(fileObj.id, { status: "error" });
            }
        },
        [ftm_id, item?.document_type_code, updateFileStatus]
    );

    // ✅ Sequential upload helper
    const uploadFilesSequentially = useCallback(
        async (filesToUpload) => {
            setFileUploading(true);
            for (const fileObj of filesToUpload) {
                await uploadFile(fileObj); // wait until the current file finishes before next
            }

            setFileUploading(false);
        },
        [uploadFile]
    );

    // ✅ Dropzone handler
    const onDrop = useCallback(
        (acceptedFiles) => {
            const newFiles = acceptedFiles.map((file, i) => {
                const fileName = file.name;
                const isImage = IMAGE_EXTENSIONS.some((ext) =>
                    fileName.toLowerCase().endsWith(ext)
                );

                const fileObj = {
                    id: `${Date.now()}-${i}`,
                    file,
                    preview: URL.createObjectURL(file),
                    fileName,
                    isImage,
                    status: "pending",
                    type: "NEW",
                };

                return fileObj;
            });

            // show them in UI immediately
            setFiles((prev) => [...newFiles, ...prev]);

            // upload one by one
            uploadFilesSequentially(newFiles);
        },
        [uploadFilesSequentially]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [], "application/pdf": [] },
        multiple: true,
    });

    // ✅ Fetch existing docs
    useEffect(() => {
        const fetchDocs = async () => {
            setLoading(true);
            try {
                const response = await fetchPersonalDocumentTypeslist(
                    ftm_id,
                    item?.document?.ftm_personal_document_type_id,
                    isGetNewDocumentFile ? "new" : "current"
                );

                if (response?.status === "Success") {
                    const docs = response?.documents?.ftm_personal_document_files || [];
                    setFiles(mapServerDocsToFiles(docs));

                    setSelectedDocument({
                        ...response?.documents,
                    });
                } else {
                    setFiles([]);
                }
            } catch (err) {
                console.error("Error fetching documents:", err);
                setFiles([]);
            } finally {
                setLoading(false);
            }
        };

        if (ftm_id && item?.document?.ftm_personal_document_type_id) fetchDocs();
    }, [ftm_id, isGetNewDocumentFile, item?.document?.ftm_personal_document_type_id, refresh]);

    // ✅ Remove document
    const removeDocument = async (file) => {
        const { id, status, ftm_personal_document_file_id } = file;

        if (status === "error") {
            URL.revokeObjectURL(file.preview);
            setFiles((prev) => prev.filter((f) => f.id !== id));
        } else {
            commonSwal({
                notificationType: "REMOVE_FTM_DOCUMENT",
                preConfirm: async () => {
                    updateFileStatus(id, { status: "uploading" });
                    return deleteDocument({
                        file_id: ftm_personal_document_file_id,
                        document_type: 2,
                    });
                },
                isConfirmed: (result) => {
                    if (result?.isConfirmed && result?.value?.status === "Success") {
                        URL.revokeObjectURL(file.preview);
                        setFiles((prev) => prev.filter((f) => f.id !== id));
                    } else {
                        updateFileStatus(id, { status: "success" });
                    }
                },
            });
        }
    };

    const hasFiles = files.length > 0;

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            centered
            title={title}
            backdrop="static"
            body={
                <section>
                    {(!isGetNewDocumentFile && documentIsExpired) && (
                        <div className="alert alert-danger alert-dismissible text-white fade show" role="alert">
                            <span className="alert-text font-weight-bold">
                                The document has passed its expiry date.
                            </span>
                        </div>
                    )}

                    {/* ✅ Comment & Expiry */}
                    {(selectedDocument?.ftm_personal_document_comment ||
                        selectedDocument?.ftm_personal_document_expiry_date) && (
                            <div className="card mb-4" style={{ boxShadow: "unset" }}>
                                <div className="card-body p-3">
                                    {selectedDocument?.ftm_personal_document_comment && (
                                        <p>
                                            <b>Comment:</b>{" "}
                                            {selectedDocument?.ftm_personal_document_comment}
                                        </p>
                                    )}
                                    {selectedDocument?.ftm_personal_document_expiry_date && (
                                        <p className="mb-0">
                                            <b>Expiry date:</b>{" "}
                                            {convertTimestampToFormat(
                                                selectedDocument?.ftm_personal_document_expiry_date
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                    {/* ✅ Approve Info */}
                    {selectedDocument?.document_approve && (
                        <div className="card mb-4" style={{ boxShadow: "unset" }}>
                            <div className="card-body p-3">
                                <p className="mb-0">
                                    Approved by admin {" "}
                                    <b>
                                        (ID: #{selectedDocument?.document_approve.admin_user.admin_id},{" "}
                                        Name:{" "}
                                        {selectedDocument?.document_approve.admin_user.admin_full_name})
                                    </b>{" "}
                                    at{" "}
                                    {convertTimestampToFormat(
                                        selectedDocument?.document_approve
                                            .ftm_personal_document_check_check_at
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ✅ Dropzone */}
                    <div {...getRootProps({ style: styles.dropzone })}>
                        <input {...getInputProps()} />
                        <p className="font-weight-bold mt-3">
                            {isDragActive
                                ? "Drop the files here..."
                                : "Drag & drop files or click to select"}
                        </p>
                    </div>

                    {/* ✅ Files */}
                    {loading ? (
                        <div style={{ height: `${window.innerHeight - 387}px` }}>
                            <DivSpinner />
                        </div>
                    ) : hasFiles ? (
                        <aside
                            style={{
                                ...styles.thumbsContainer,
                                justifyContent: window.innerWidth < 770 ? "center" : "",
                                // height: files.length >= 14 ? "450px" : "",
                                // overflow: files.length >= 14 ? "auto" : "",
                            }}
                        >
                            {files.map((file) => (
                                <FileItem
                                    key={file.id}
                                    file={file}
                                    onRemove={removeDocument}
                                    handleFileViewOpen={handleFileViewOpen}
                                />
                            ))}

                            <FilesView
                                show={showFileViewModal}
                                handleClose={handleFileViewClose}
                                title={item?.document_type_name + " files"}
                                files={files}
                                selectedId={fileViewIndex}
                            />
                        </aside>
                    ) : (
                        <div style={styles.emptyState}>
                            <i
                                className="material-icons"
                                style={{ fontSize: 64, color: "#ccc" }}
                            >
                                folder_open
                            </i>
                            <p style={{ fontSize: 14, color: "#777" }}>
                                No files uploaded yet
                            </p>
                        </div>
                    )}

                    {!loading && (
                        <CheckLists
                            list={selectedDocument?.personal_document_type?.personal_document_type_check_list} 
                            type={"personalDocument"} 
                            isApprove={selectedDocument?.ftm_personal_document_is_approve === 1}
                            approveDocumentDetails={selectedDocument?.document_approve}
                            accepted={acceptCheckList}
                            handleAccepted={setAcceptCheckList}
                            showApprove={(!isGetNewDocumentFile && documentIsExpired)}
                        />
                    )}
                </section>
            }
            footer={
                <ActionBtnList
                    ftm_id={ftm_id}
                    document_type="personal"
                    selectedDocument={selectedDocument}
                    handleRefresh={() => {
                        personalDocumentsHandleRefresh();
                        setRefresh((prev) => prev + 1);
                    }}
                    acceptCheckList={acceptCheckList}
                    documentIsExpired={!isGetNewDocumentFile && documentIsExpired}
                    approveNewDocument={isGetNewDocumentFile}
                    closeModal={handleClose}
                />
            }
        />
    );
};

export default PersonalDocumentsFiles;